import { HTMLAttributes, useCallback, useEffect, useRef, useState } from 'react'
import { useKey } from 'react-use'

import SVGIcon from '@/components/base/svg-icon'
import Typography from '@/components/base/typography'
import { useStore } from '@/contexts/store'
import { useLocaleStore } from '@/store/locale'
import { LANGUAGES } from '@/constants'

import { cn } from '@/lib/utils'

type LangSwitcherProps = HTMLAttributes<HTMLDivElement>

const LangSwitcher = ({ className, ...props }: LangSwitcherProps) => {
  const currentLocale = useLocaleStore((state) => state.currentLocale)
  const currentTranslations = useLocaleStore(
    (state) => state.currentTranslations
  )
  const { strings } = useStore()

  const [open, setOpen] = useState(false)
  const rootRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const itemsRef = useRef<(HTMLAnchorElement | null)[]>([])
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const focusIndexRef = useRef(-1)
  const isOpeningRef = useRef(false)

  const otherLanguages = LANGUAGES.filter(
    (lang: string) => lang !== currentLocale
  )

  const getHref = useCallback(
    (lang: string) =>
      currentTranslations?.find((t: { locale: string }) =>
        t.locale.includes(lang)
      )?.href || `/${lang}`,
    [currentTranslations]
  )

  // ── Focus helpers ──

  const focusItem = useCallback(
    (index: number) => {
      const clamped = Math.max(0, Math.min(index, otherLanguages.length - 1))
      focusIndexRef.current = clamped
      itemsRef.current[clamped]?.focus()
    },
    [otherLanguages.length]
  )

  const openAndFocusFirst = useCallback(() => {
    isOpeningRef.current = true
    setOpen(true)
    // Focus primo item al prossimo frame, dopo che il DOM e' visibile
    requestAnimationFrame(() => focusItem(0))
    // Previeni click accidentali per 150ms dopo l'apertura
    setTimeout(() => {
      isOpeningRef.current = false
    }, 150)
  }, [focusItem])

  const openAndFocusLast = useCallback(() => {
    isOpeningRef.current = true
    setOpen(true)
    requestAnimationFrame(() => focusItem(otherLanguages.length - 1))
    setTimeout(() => {
      isOpeningRef.current = false
    }, 150)
  }, [focusItem, otherLanguages.length])

  const close = useCallback(() => {
    setOpen(false)
    focusIndexRef.current = -1
    triggerRef.current?.focus()
  }, [])

  // ── Close on Escape ──
  useKey(
    'Escape',
    () => {
      if (open) close()
    },
    {},
    [open, close]
  )

  // ── Close on click outside ──
  useEffect(() => {
    if (!open) return

    const handleClickOutside = (e: MouseEvent | TouchEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false)
        focusIndexRef.current = -1
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    document.addEventListener('touchstart', handleClickOutside)

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('touchstart', handleClickOutside)
    }
  }, [open])

  // ── Cleanup timer on unmount ──
  useEffect(() => {
    return () => {
      if (closeTimerRef.current) clearTimeout(closeTimerRef.current)
    }
  }, [])

  // ── Hover handlers (desktop only, ignored on touch) ──
  const onPointerEnter = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (e.pointerType !== 'mouse') return

      if (closeTimerRef.current) {
        clearTimeout(closeTimerRef.current)
        closeTimerRef.current = null
      }
      setOpen(true)
    },
    []
  )

  const onPointerLeave = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (e.pointerType !== 'mouse') return

      closeTimerRef.current = setTimeout(() => {
        setOpen(false)
        focusIndexRef.current = -1
      }, 200)
    },
    []
  )

  // ── Trigger keyboard handler ──
  const onTriggerKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      switch (e.key) {
        case 'ArrowDown':
        case 'Enter':
        case ' ':
          e.preventDefault()
          openAndFocusFirst()
          break
        case 'ArrowUp':
          e.preventDefault()
          openAndFocusLast()
          break
      }
    },
    [openAndFocusFirst, openAndFocusLast]
  )

  // ── Menu item keyboard handler ──
  const onItemKeyDown = useCallback(
    (e: React.KeyboardEvent, index: number) => {
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault()
          if (index < otherLanguages.length - 1) {
            focusItem(index + 1)
          } else {
            focusItem(0)
          }
          break
        case 'ArrowUp':
          e.preventDefault()
          if (index > 0) {
            focusItem(index - 1)
          } else {
            focusItem(otherLanguages.length - 1)
          }
          break
        case 'Home':
          e.preventDefault()
          focusItem(0)
          break
        case 'End':
          e.preventDefault()
          focusItem(otherLanguages.length - 1)
          break
        case 'Tab':
          close()
          break
      }
    },
    [otherLanguages.length, focusItem, close]
  )

  const toggle = useCallback(() => {
    if (open) {
      close()
    } else {
      setOpen(true)
    }
  }, [open, close])

  return (
    <div
      ref={rootRef}
      className={cn('relative mr-[4px]', className)}
      onPointerEnter={onPointerEnter}
      onPointerLeave={onPointerLeave}
      {...props}
    >
      <button
        ref={triggerRef}
        type="button"
        onClick={toggle}
        onKeyDown={onTriggerKeyDown}
        aria-expanded={open}
        aria-haspopup="true"
        aria-controls="lang-switcher-menu"
        aria-label={strings?.labels?.language_navigation || 'Select language'}
        className={cn('relative z-20', 'flex items-center gap-spacing-2xs', {
          'text-text-primary-light': open
        })}
      >
        <Typography variant="menu" className="w-[20px] font-normal uppercase">
          {currentLocale || 'it'}
        </Typography>
        <SVGIcon
          symbol="arrow-down"
          className={cn(
            'size-[16px] fill-current',
            'transition-transform duration-100 ease-in-out-cubic',
            { 'rotate-180': open }
          )}
        />
      </button>

      <nav
        id="lang-switcher-menu"
        aria-label={strings?.labels?.language_navigation || 'Select language'}
        aria-hidden={!open}
        inert={!open ? true : undefined}
        className={cn(
          'absolute -top-spacing-2xs left-[-8px] z-10 w-[calc(100%+16px)]',
          'ease-in-out transition-[opacity,visibility] duration-200',
          open ? 'visible opacity-100' : 'invisible opacity-0'
        )}
      >
        <ul
          role="menu"
          className="m-0 flex flex-col gap-spacing-2xs rounded-radius-minimal bg-surface-primary-dark p-spacing-2xs pt-[30px] shadow-[0_2px_8px_rgba(0,0,0,0.12)]"
        >
          {otherLanguages.map((lang: string, index: number) => (
            <li key={lang} role="none">
              <a
                ref={(el) => {
                  itemsRef.current[index] = el
                }}
                href={getHref(lang)}
                role="menuitem"
                tabIndex={open ? 0 : -1}
                onClick={(e) => {
                  if (isOpeningRef.current) {
                    e.preventDefault()
                  }
                }}
                onKeyDown={(e) => onItemKeyDown(e, index)}
                className={cn(
                  'text-menu leading-0 text-trim block uppercase text-text-primary-light',
                  'ease-in-out transition-opacity duration-300',
                  'hover:opacity-50',
                  'focus-visible:opacity-50'
                )}
              >
                {lang}
              </a>
            </li>
          ))}
        </ul>
      </nav>
    </div>
  )
}

export default LangSwitcher
